/**
 * WEB-1905 (остаток от приёмки 07.09) — реестр СТАРЫХ английских умолчаний
 * портала подписчика.
 *
 * Откуда он взялся. До правки 05.09 (`bd5c567e0`) конструктор писал подписи
 * портальных блоков прямо в содержимое: `subscriptionListTitle: "Your
 * subscriptions"`, `cancelButtonLabel: "Cancel subscription"` и т.д. (см.
 * `store/editorSlice.ts` на срезе `bd5c567e0^`). Правка научила конструктор
 * так больше не делать, а рендер — брать слово из словаря плеера по языку
 * воронки в ПУСТОЕ поле. Но у всех пейволлов, заведённых ДО 05.09, поля
 * остались заполненными английским, и заполненное поле выигрывает у словаря
 * всегда: покупатель русской воронки читал `Your subscriptions` рядом с
 * русской датой продления (приёмка 07.09, снимок `1905-portal-ru-mixed-lang`).
 *
 * Решение владельца — путь 2 из комментария приёмки: НЕ переносить данные, а
 * считать содержимое, точно равное старому умолчанию, пустым. Перечень живёт
 * в ОДНОМ месте — здесь, — и не рассыпается по компонентам.
 *
 * Сравнение СТРОГО на точное совпадение (без обрезки пробелов и без
 * приведения регистра): авторский текст, чем-либо отличающийся от нашего
 * умолчания, — это выбор автора, и трогать его нельзя.
 *
 * Два конца правила — сторож `portalLegacyDefaults.twoEnds.web1905.guard.test.ts`:
 * (1) множество реестра = множеству английских фолбэков словаря портала в обе
 * стороны; (2) каждый фолбэк в `components/editor/portal/**` идёт через
 * `resolvePortalText`, а не через сырое `label || t.cancelLabel` (второй конец
 * выводится обходом исходников, не ручным списком).
 */

/**
 * Английские умолчания, которые конструктор писал в содержимое портальных
 * блоков до 05.09. Сегодня они байт-в-байт равны английскому словарю портала
 * (`publicQuizStrings` → `portal.listTitle` и соседние), и сторож держит это
 * равенство. Если английскую подпись когда-нибудь перепишут, прежняя строка
 * ОСТАЁТСЯ здесь: это история содержимого старых пейволлов, а не сегодняшний
 * текст, — сторож в этот момент станет красным и потребует её записать.
 */
export const LEGACY_PORTAL_DEFAULT_TEXTS = Object.freeze([
  /** Заголовок блока «Портал: список подписок» (`subscriptionListTitle`). */
  'Your subscriptions',
  /** Надпись кнопки отмены (`cancelButtonLabel`). */
  'Cancel subscription',
  /** Надпись кнопки возобновления (`resumeButtonLabel`, WEB-1395). */
  'Resume subscription',
  /** Заголовок оффера удержания (`downsellOfferTitle`). */
  'Wait — here is a discount',
  /** Кнопка оффера удержания (`downsellOfferCtaLabel`). */
  'Accept discount',
] as const);

const LEGACY_SET: ReadonlySet<string> = new Set(LEGACY_PORTAL_DEFAULT_TEXTS);

/**
 * Правда ли, что это ровно наше старое английское умолчание, а не авторский
 * текст. Пустое значение — не умолчание (оно и так падает в словарь).
 */
export function isLegacyPortalDefault(
  value: string | null | undefined,
): boolean {
  if (!value) return false;
  return LEGACY_SET.has(value);
}

/**
 * Подпись портального блока: авторская, если автор её задал, иначе слово из
 * словаря по языку воронки. Старое английское умолчание в содержимом считается
 * НЕзаданным — ради этого правила всё и написано.
 *
 * На английской воронке поведение не меняется ни на байт: старое умолчание и
 * сегодняшний английский словарь — одна и та же строка.
 */
export function resolvePortalText(
  authored: string | null | undefined,
  fallback: string,
): string {
  return authored && !isLegacyPortalDefault(authored) ? authored : fallback;
}

/**
 * Ревизия пачки 29 (12.09.2026): тот же класс у блоков ПЕЙВОЛЛА. Конструктор до
 * 05.09 писал эти английские умолчания прямо в содержимое
 * (`bd5c567e0^:frontend/src/store/editorSlice.ts:2806-2814`); заполненное поле
 * бьёт словарь, и покупатель русской воронки на старом пейволле читал
 * «Wait — here is a special offer» посреди русского. Список отдельный от
 * портального: у него другой словарь (`blockDefaults` / `paymentTermsTemplate`),
 * и сторож портала сверяет СВОЙ список со СВОИМ словарём.
 */
export const LEGACY_PAYWALL_DEFAULT_TEXTS = Object.freeze([
  /** Шаблон блока «Условия оплаты» (`paymentTermsTemplate`). */
  'Billed {{price}} monthly. Cancel anytime.',
  /** Заголовок попапа даунселла (`downsellTitle`). */
  'Wait — here is a special offer',
  /** Кнопка попапа даунселла (`downsellCtaLabel`). */
  'Get discount',
] as const);

const LEGACY_PAYWALL_SET: ReadonlySet<string> = new Set(
  LEGACY_PAYWALL_DEFAULT_TEXTS,
);

/**
 * Поле блока пейволла: авторское, если автор его задал, иначе слово из словаря
 * по языку воронки; старое английское умолчание считается НЕзаданным. Работает
 * только со строкой — богатую разметку (RichField-объект) не трогает: такое
 * значение автор набрал сам.
 */
export function resolvePaywallText<T>(
  authored: T,
  fallback: string,
): T | string {
  if (typeof authored === 'string' && LEGACY_PAYWALL_SET.has(authored)) {
    return fallback;
  }
  return authored;
}
